/**
 * Utility functions for determining invoice column visibility based on tenant plan type
 */

/**
 * Determines which columns should be visible in invoice displays
 * @param {Object} tenant - Tenant object with accountPremiumStatus
 * @param {Array} invoiceData - Invoice line items to check for mid-month plan changes
 * @returns {Object} Column visibility configuration
 */
export const getInvoiceColumnVisibility = (tenant, invoiceData = []) => {
  const premiumStatus = tenant?.accountPremiumStatus || [];
  const bundleColumnName = "Hera Subscription";
  const allVisibleColumns = [
    "standard",
    "performance",
    "rostering",
    "staff",
    "vehicles",
  ];
  let visibleColumns = [];
  // Extract plan types from premium status
  const planTypes = premiumStatus.filter((plan) =>
    [
      "trial",
      "none",
      "bundle",
      "unpaid",
      "standard",
      "performance",
      "rostering",
      "staff",
      "vehicles",
    ].includes(plan)
  );

  // Check for mid-month plan change first
  const hasMidMonthChange = checkMidMonthPlanChange(invoiceData);
  // Check if customer has bundle plan or is in trial
  const hasBundlePlan = planTypes.includes("bundle");
  const isTrialCustomer = planTypes.includes("trial") || invoiceData.some((item) => item.isTrial);


  if (hasMidMonthChange) {
    // Mid-month plan change: show all columns including Hera Subscription
    visibleColumns = [...allVisibleColumns, "heraSubscription"];
  } else if (hasBundlePlan || isTrialCustomer) {
    // Bundle/Trial customers: show only Hera Subscription column
    visibleColumns = ["heraSubscription"];
  } else {
    // Non-bundle customers: show all individual columns (no Hera Subscription)
    visibleColumns = allVisibleColumns;
  }

  return {
    visibleColumns,
    bundleColumnName,
  };
};

/**
 * Checks if there was a plan change during the invoice period
 * @param {Array} invoiceData - Invoice line items
 * @returns {boolean} True if plan changed mid-month
 */
const checkMidMonthPlanChange = (invoiceData) => {
  if (!invoiceData || invoiceData.length === 0) return false;

  // Check if there are both bundle and non-bundle charges in the same period
  const hasBundleCharges = invoiceData.some((item) => item.bundleCostExt > 0);
  const hasNonBundleCharges = invoiceData.some(
    (item) =>
      item.standardCostExt > 0 ||
      item.performanceCostExt > 0 ||
      item.rosteringCostExt > 0 ||
      item.staffCostExt > 0 ||
      item.vehiclesCostExt > 0
  );
  return hasBundleCharges && hasNonBundleCharges;
};
